import {
  Avatar,
  Box,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
  Stack,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";

//... Import Azure services
import { baseUrl } from "../../services/AzureStorage";

//... Import common entities
import Helper from "../../common/Helper";
import RouteConstants from "../../common/RouteConstants";

//... Imports context provider.
import { AppDataContext } from "../../common/appContext";

//... Imports components.
import IndividualCombineStatsBarChart from "./IndividualCombineStatsBarChart";

//... Imports Shared components.
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "../../sharedComponents/Accordion/Accordion";
import TitleTooltip from "../../sharedComponents/Tooltip/TitleTooltip";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: "5px 15px",
    border: "1px solid lightgrey",
    borderRadius: "10px",
    boxShadow: "2px 2px 11px grey inset",
  },
  listItemIcon: {
    minWidth: "38px",
  },
  imageIcon: {
    width: "100%",
    height: "100%",
    cursor: "pointer",
  },
  iconRoot: {
    textAlign: "center",
    width: "100%",
    height: "50px",
  },
  cardStyle: {
    border: "1px solid lightgrey",
    borderRadius: "10px",
    height: "250px",
  },

  headerText: {
    marginBottom: theme.spacing(0.5),
    color: "rgb(29, 66, 138)",
  },
  headerText2: {
    marginBottom: theme.spacing(0.5),
    color: "rgb(29, 66, 138)",
  },
  headerTextInfo: {
    color: "rgb(29, 66, 138)",
    fontSize: "14px",
  },
  headerBox: {
    background: "#c1d0ed",
  },
  smBlue: {
    marginLeft: "16px",
    width: 26,
    height: 26,
    color: theme.palette.getContrastText("#0c2c75"),
    backgroundColor: "#1e3f7e",
    position: "relative",
    overflow: "hidden",
    borderRadius: "50%",
  },
  playerNameStyle: {
    cursor: "pointer",
    "&:hover": {
      color: "blue",
    },
  },
  dataField: {
    backgroundColor: "#1d428a",
    color: "white",
    width: "fit-content",
    padding: "3px",
    fontSize: "0.875rem",
    fontWeight: "400",
  },

  percentileField: {
    width: "fit-content",
    padding: "3px",
    fontSize: "0.875rem",
    fontWeight: "bold",
  },
  cellContent: {
    padding: "5px",
    textAlign: "center",
  },
  percentileCellContent: {
    display: "flex",
    justifyContent: "center",
  },
  customDivider: {
    marginTop: "8px",
    marginBottom: "8px",
    "&.MuiDivider-root": {
      "&::before": {
        borderTop: "4px solid #1d428a",
      },
      "&::after": {
        borderTop: "4px solid #1d428a",
      },
    },
    "& .MuiDivider-wrapper": {
      fontSize: 16,
    },
  },
}));

const defaultColorSchema = "rgb(186 28 28 / 87%)";
const IndividualPercentileStrengthAndAgility = ({
  eventId,
  loading,
  strengthAndAgility,
  validateNumber,
  strengthAndAgilityPercentileStats,
  strengthAndAgilityAVGStats,
  colorSchemaForGroups,
  expandedGroups,
  handleGroupAccordionChange,
}) => {
  const classes = useStyles();
  let navigate = useNavigate();

  const {
    getHandDimensionsUnitValue,
    getHandDimensionsUnitLabelType,
    lengthUnits,
  } = useContext(AppDataContext);

  const [graphDataSet, setGraphDataSet] = useState({});
  const [avgGraphDataSet, setAvgGraphDataSet] = useState({});

  const [chartType, setChartType] = useState("avg");

  const handleChangeChartType = (event) => {
    setChartType(event.target.value);
  };

  useEffect(() => {
    if (strengthAndAgilityPercentileStats?.length > 0) {
      setGraphDataSet(preapreGraphDataSet(strengthAndAgilityPercentileStats));
    }
  }, [strengthAndAgilityPercentileStats]);

  useEffect(() => {
    if (strengthAndAgilityAVGStats?.length > 0) {
      setAvgGraphDataSet(preapreGraphDataSet(strengthAndAgilityAVGStats));
    }
  }, [strengthAndAgilityAVGStats]);

  const preapreGraphDataSet = (inputData) => {
    if (!inputData || inputData.length === 0)
      return { labels: [], datasets: [] };

    const groups = Object.keys(inputData[0]).filter((key) =>
      key.includes("group")
    );

    const datasets = groups.map((group, index) => ({
      label: `Group - ${index + 1}`,
      data: formatData(inputData, group),
      backgroundColor: colorSchemaForGroups[index] || defaultColorSchema,
      borderColor: colorSchemaForGroups[index] || defaultColorSchema,
      borderWidth: 1,
      fill: false,
    }));

    return { labels: inputData.map((item) => item.category), datasets };
  };

  function formatData(inputData, group) {
    return inputData.map((item) => {
      const floatValue = parseFloat(item[group]);
      return Number.isNaN(floatValue) ||
        floatValue < 0 ||
        floatValue > 1 ||
        floatValue === 0
        ? floatValue
        : floatValue.toFixed(2);
    });
  }

  const viewPlayerProfile = (playerId) => {
    navigate(`/${eventId}${RouteConstants.VIEW_PLAYER_PROFILE}/${playerId}`, {
      state: { from: "CombineResults", tab: 0 },
    });
  };

  const historicalPercentileSASColumn = [
    {
      id: "Player Name",
      headerName: "Player Name",
      style: { padding: "4px", paddingLeft: "25px", width: "200px" },
      unit: "",
    },
    {
      id: "Position",
      headerName: "Position",
      style: { padding: "4px", textAlign: "center", width: "100px" },
      unit: "",
    },
    {
      id: "College Name",
      headerName: "College Name",
      style: { padding: "4px", textAlign: "center", width: "200px" },
      unit: "",
    },
    {
      id: "Lane Shuttle Left",
      headerName: "Lane Shuttle Left",
      style: {
        padding: "4px",
        textAlign: "center",
        verticalAlign: "top",
        width: "200px",
      },
      unit: `(sec)`,
    },
    {
      id: "Lane Shuttle Right",
      headerName: "Lane Shuttle Right",
      style: {
        padding: "4px",
        textAlign: "center",
        verticalAlign: "top",
        width: "200px",
      },
      unit: `(sec)`,
    },
    {
      id: "3/4 Court Sprint",
      headerName: "3/4 Court Sprint",
      style: {
        padding: "4px",
        textAlign: "center",
        verticalAlign: "top",
        width: "200px",
      },
      unit: `(sec)`,
    },
    {
      id: "Pro Lane",
      headerName: "Pro Lane",
      style: {
        padding: "4px",
        textAlign: "center",
        verticalAlign: "top",
        width: "200px",
      },
      unit: `(sec)`,
    },
    {
      id: "Max Vertical Jump",
      headerName: "Max Vertical Jump",
      style: {
        padding: "4px",
        textAlign: "center",
        verticalAlign: "top",
        width: "200px",
      },
      unit: `${getHandDimensionsUnitLabelType(lengthUnits)}`,
    },
    {
      id: "Standing Vertical",
      headerName: "Standing Vertical",
      style: {
        padding: "4px",
        textAlign: "center",
        verticalAlign: "top",
        width: "200px",
      },
      unit: `${getHandDimensionsUnitLabelType(lengthUnits)}`,
    },
  ];

  const RenderChartFilterSection = () => {
    return (
      <Box display="flex" justifyContent="center">
        <FormControl component="fieldset">
          <RadioGroup row value={chartType} onChange={handleChangeChartType}>
            <FormControlLabel value="avg" control={<Radio />} label="AVG" />
            <FormControlLabel
              value="percentile"
              control={<Radio />}
              label="Percentile"
            />
          </RadioGroup>
        </FormControl>
      </Box>
    );
  };

  const RenderAvgTableRow = ({ avg }) => {
    return (
      avg &&
      Object.keys(avg).length > 0 && (
        <TableRow
          sx={{
            backgroundColor: "#F5F5F5",
          }}
        >
          <TableCell className={classes.cellContent}></TableCell>
          <TableCell className={classes.cellContent}></TableCell>
          <TableCell className={classes.cellContent}>
            <Typography variant="subtitle2" display="block" gutterBottom>
              {"AVG"}
            </Typography>
          </TableCell>
          <TableCell className={classes.cellContent}>
            {avg.shuttleRunLeftTrialTime
              ? validateNumber(avg.shuttleRunLeftTrialTime)
              : 0}
          </TableCell>
          <TableCell className={classes.cellContent}>
            {avg.shuttleRunRightTrialTime
              ? validateNumber(avg.shuttleRunRightTrialTime)
              : 0}
          </TableCell>
          <TableCell className={classes.cellContent}>
            {avg.threeFourRunTime ? validateNumber(avg.threeFourRunTime) : 0}
          </TableCell>
          <TableCell className={classes.cellContent}>
            {avg.proLaneTrialTime ? validateNumber(avg.proLaneTrialTime) : 0}
          </TableCell>
          <TableCell className={classes.cellContent}>
            {avg.maxVerticalLeap
              ? getHandDimensionsUnitValue(validateNumber(avg.maxVerticalLeap))
              : 0}
          </TableCell>
          <TableCell className={classes.cellContent}>
            {avg.standingVerticalLeap
              ? getHandDimensionsUnitValue(
                  validateNumber(avg.standingVerticalLeap)
                )
              : 0}
          </TableCell>
        </TableRow>
      )
    );
  };

  const RenderPercentileTableRow = ({ percentile }) => {
    return (
      false &&
      percentile &&
      Object.keys(percentile).length > 0 && (
        <TableRow
          sx={{
            backgroundColor: "#F5F5F5",
          }}
        >
          <TableCell className={classes.cellContent}></TableCell>
          <TableCell className={classes.cellContent}></TableCell>
          <TableCell className={classes.cellContent}>
            <Typography variant="subtitle2" display="block" gutterBottom>
              {"PERCENTILE"}
            </Typography>
          </TableCell>
          <TableCell className={classes.cellContent}>
            <Box className={classes.percentileCellContent}>
              <Box className={classes.dataField}>
                {percentile.shuttleRunLeftTrialTime
                  ? `${percentile.shuttleRunLeftTrialTime}%`
                  : "-"}
              </Box>
            </Box>
          </TableCell>
          <TableCell className={classes.cellContent}>
            <Box className={classes.percentileCellContent}>
              <Box className={classes.dataField}>
                {percentile.shuttleRunRightTrialTime
                  ? `${percentile.shuttleRunRightTrialTime}%`
                  : "-"}
              </Box>
            </Box>
          </TableCell>
          <TableCell className={classes.cellContent}>
            <Box className={classes.percentileCellContent}>
              <Box className={classes.dataField}>
                {percentile.threeFourRunTime
                  ? `${percentile.threeFourRunTime}%`
                  : "-"}
              </Box>
            </Box>
          </TableCell>
          <TableCell className={classes.cellContent}>
            <Box className={classes.percentileCellContent}>
              <Box className={classes.dataField}>
                {percentile.proLaneTrialTime
                  ? `${percentile.proLaneTrialTime}%`
                  : "-"}
              </Box>
            </Box>
          </TableCell>
          <TableCell className={classes.cellContent}>
            <Box className={classes.percentileCellContent}>
              <Box className={classes.dataField}>
                {percentile.maxVerticalLeap
                  ? `${percentile.maxVerticalLeap}%`
                  : "0%"}
              </Box>
            </Box>
          </TableCell>
          <TableCell className={classes.cellContent}>
            <Box className={classes.percentileCellContent}>
              <Box className={classes.dataField}>
                {percentile.standingVerticalLeap
                  ? `${percentile.standingVerticalLeap}%`
                  : "0%"}
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      )
    );
  };

  return (
    <>
      <Container maxWidth={false}>
        <Box p={1}>
          <Box sx={{ marginTop: "1.5rem" }}>
            {strengthAndAgility &&
              strengthAndAgility.map((groups, index) => {
                const { groupID, data, avg, percentile } = groups;
                return (
                  <React.Fragment key={index}>
                    <Accordion
                      expanded={
                        expandedGroups?.strengthAndAgility?.[index]?.expanded
                      }
                      onChange={() =>
                        handleGroupAccordionChange("strengthAndAgility", index)
                      }
                      borderColor={`${colorSchemaForGroups[index]}`}
                    >
                      <AccordionSummary
                        aria-controls="panel2d-content"
                        id="panel2d-header"
                        className="accordion-sub-header"
                        color={`${colorSchemaForGroups[index]}`}
                        expanded={
                          expandedGroups?.strengthAndAgility?.[index]?.expanded
                        }
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box className={classes.headerBox}>
                            <Typography
                              className={`${classes.headerText} accordionTitle`}
                              style={{
                                color: `${colorSchemaForGroups[index]}`,
                              }}
                            >
                              {`Group - ${groupID}`.toUpperCase()}
                            </Typography>
                          </Box>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box p={2}>
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  {historicalPercentileSASColumn.map(
                                    (col, index) => (
                                      <TitleTooltip
                                        title={col.id}
                                        placement="top-start"
                                        key={index}
                                      >
                                        <TableCell style={col.style}>
                                          {col.headerName}
                                        </TableCell>
                                      </TitleTooltip>
                                    )
                                  )}
                                </TableRow>
                              </TableHead>
                              <TableHead>
                                <TableRow>
                                  {historicalPercentileSASColumn.map(
                                    (col, index) => (
                                      <TableCell style={col.style} key={index}>
                                        {col.unit}
                                      </TableCell>
                                    )
                                  )}
                                </TableRow>
                              </TableHead>
                              {data && data.length > 0 ? (
                                <>
                                  <TableBody>
                                    {/* Display Comparable Group Datasets for Anthropometry */}
                                    {data?.map((val, index) => {
                                      return (
                                        <TableRow key={index}>
                                          <TableCell
                                            className={classes.stickyColumn}
                                            sx={{ padding: "4px" }}
                                          >
                                            <Box display="flex">
                                              {val.headshotURL &&
                                              val.headshotURL !== "" ? (
                                                <Avatar
                                                  className={classes.smBlue}
                                                  sx={{
                                                    bgcolor: "#1d428a",
                                                    cursor: "pointer",
                                                  }}
                                                  src={
                                                    val.headshotURL
                                                      ? baseUrl(val.headshotURL)
                                                      : ""
                                                  }
                                                  alt="basketball"
                                                  onClick={() =>
                                                    viewPlayerProfile(
                                                      val.playerID
                                                    )
                                                  }
                                                >
                                                  <Typography variant="caption">
                                                    {Helper.GetNameInitials(
                                                      `${val.playerName}`
                                                    )}
                                                  </Typography>
                                                </Avatar>
                                              ) : (
                                                <Avatar
                                                  className={classes.smBlue}
                                                  sx={{
                                                    bgcolor: "#1d428a",
                                                    cursor: "pointer",
                                                  }}
                                                  alt="basketball"
                                                  onClick={() =>
                                                    viewPlayerProfile(
                                                      val.playerID
                                                    )
                                                  }
                                                >
                                                  <Typography variant="caption">
                                                    {Helper.GetNameInitials(
                                                      `${val.playerName}`
                                                    )}
                                                  </Typography>
                                                </Avatar>
                                              )}
                                              &nbsp;&nbsp;
                                              <TitleTooltip
                                                title="View Player Profile"
                                                placement="right-start"
                                              >
                                                <Typography
                                                  variant="body2"
                                                  className={
                                                    classes.playerNameStyle
                                                  }
                                                  onClick={() =>
                                                    viewPlayerProfile(
                                                      val.playerID
                                                    )
                                                  }
                                                >
                                                  {val.playerName}
                                                </Typography>
                                              </TitleTooltip>
                                            </Box>
                                          </TableCell>
                                          <TableCell
                                            className={classes.cellContent}
                                          >
                                            {val.position ? val.position : "-"}
                                          </TableCell>
                                          <TableCell
                                            className={classes.cellContent}
                                          >
                                            {val.collegeOrClub
                                              ? val.collegeOrClub
                                              : "-"}
                                          </TableCell>
                                          <TableCell
                                            className={classes.cellContent}
                                          >
                                            {val.shuttleRunLeftTrialTime
                                              ? validateNumber(
                                                  val.shuttleRunLeftTrialTime
                                                )
                                              : 0}
                                          </TableCell>
                                          <TableCell
                                            className={classes.cellContent}
                                          >
                                            {val.shuttleRunRightTrialTime
                                              ? validateNumber(
                                                  val.shuttleRunRightTrialTime
                                                )
                                              : 0}
                                          </TableCell>
                                          <TableCell
                                            className={classes.cellContent}
                                          >
                                            {val.threeFourRunTime
                                              ? validateNumber(
                                                  val.threeFourRunTime
                                                )
                                              : 0}
                                          </TableCell>
                                          <TableCell
                                            className={classes.cellContent}
                                          >
                                            {val.proLaneTrialTime
                                              ? validateNumber(
                                                  val.proLaneTrialTime
                                                )
                                              : 0}
                                          </TableCell>
                                          <TableCell
                                            className={classes.cellContent}
                                          >
                                            {getHandDimensionsUnitValue(
                                              val.maxVerticalLeap
                                                ? validateNumber(
                                                    val.maxVerticalLeap
                                                  )
                                                : 0
                                            )}
                                          </TableCell>
                                          <TableCell
                                            className={classes.cellContent}
                                          >
                                            {getHandDimensionsUnitValue(
                                              val.standingVerticalLeap
                                                ? validateNumber(
                                                    val.standingVerticalLeap
                                                  )
                                                : 0
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}

                                    {/* Display Comparable Group Average Details for Anthropometry */}
                                    <RenderAvgTableRow avg={avg} />

                                    {/* Display Comparable Group Percentile Details for Anthropometry */}
                                    <RenderPercentileTableRow
                                      percentile={percentile}
                                    />
                                  </TableBody>
                                </>
                              ) : (
                                <>
                                  <TableBody>
                                    <TableRow>
                                      <TableCell colSpan={10} align="center">
                                        No Records Found
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </>
                              )}
                            </Table>
                          </TableContainer>
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    {/* Conditionally render the Divider if the current item is not the last item */}
                    {index !== strengthAndAgility.length - 1 && (
                      <Divider className={classes.customDivider} sx={{ my: 2 }}>
                        VS
                      </Divider>
                    )}
                  </React.Fragment>
                );
              })}
          </Box>

          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ my: 2 }}
          >
            <Grid item md={7} sm={12} xs={12} justifyContent="center">
              <RenderChartFilterSection />

              {Object.keys(graphDataSet).length > 0 &&
                chartType === "percentile" && (
                  <IndividualCombineStatsBarChart
                    chartName={"Percentile Chart"}
                    data={graphDataSet}
                    max={120}
                  />
                )}

              {Object.keys(avgGraphDataSet).length > 0 &&
                chartType === "avg" && (
                  <IndividualCombineStatsBarChart
                    chartName={"AVG Chart"}
                    data={avgGraphDataSet}
                    max={500}
                    chartType={"AVG"}
                  />
                )}
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default IndividualPercentileStrengthAndAgility;
